import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Clock,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { getCRMStages } from '../../lib/leadPipelineRegistry';
import { getStageSLAs } from '../../lib/slaCatalogueRegistry';

// ── helpers ──────────────────────────────────────────────────────────────────

const isAr = () => document.documentElement.lang === 'ar';

// ── KPI category definitions ──────────────────────────────────────────────────

interface KPICategory {
  id: string;
  label: string;
  labelAr: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  descriptionAr: string;
  metrics: { name: string; nameAr: string; target: string; owner: string; ownerAr: string }[];
}

const kpiCategories: KPICategory[] = [
  {
    id: 'speed',
    label: 'Speed & Responsiveness',
    labelAr: 'السرعة والاستجابة',
    icon: Zap,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    description: 'How fast the team responds to new leads and moves through early stages',
    descriptionAr: 'مدى سرعة الفريق في الاستجابة للعملاء الجدد والتقدم في المراحل الأولى',
    metrics: [
      { name: 'Speed-to-lead (digital)', nameAr: 'سرعة الوصول للعميل (رقمي)', target: '<3 min', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
      { name: 'Speed-to-lead (other channels)', nameAr: 'سرعة الوصول (قنوات أخرى)', target: '<10 min', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
      { name: 'Time from capture to assignment', nameAr: 'وقت الالتقاط حتى التعيين', target: '<30 sec', owner: 'CRM System', ownerAr: 'نظام CRM' },
      { name: 'WhatsApp first response', nameAr: 'أول رد عبر واتساب', target: '<5 min', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
    ],
  },
  {
    id: 'conversion',
    label: 'Conversion & Pipeline',
    labelAr: 'التحويل ومسار المبيعات',
    icon: TrendingUp,
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    description: 'Lead-to-sale conversion rates and stage-by-stage funnel efficiency',
    descriptionAr: 'معدلات تحويل العملاء للمبيعات وكفاءة مسار المبيعات في كل مرحلة',
    metrics: [
      { name: 'Contact rate (cadence)', nameAr: 'معدل التواصل (التسلسل)', target: '>30%', owner: 'BDC Team', ownerAr: 'فريق BDC' },
      { name: 'Qualification rate', nameAr: 'معدل التأهيل', target: '>40% of contacted', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
      { name: 'Appointment booking rate', nameAr: 'معدل حجز المواعيد', target: '>85%', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
      { name: 'Show rate', nameAr: 'معدل الحضور', target: '>70%', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'Showroom-to-quote rate', nameAr: 'معدل تحويل المعرض للعروض', target: '>70%', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'Negotiation-to-booking rate', nameAr: 'معدل تحويل التفاوض للحجز', target: '>60%', owner: 'Sales Manager', ownerAr: 'مدير المبيعات' },
    ],
  },
  {
    id: 'quality',
    label: 'Quality & Compliance',
    labelAr: 'الجودة والامتثال',
    icon: CheckCircle2,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Data quality, SLA compliance, and process adherence metrics',
    descriptionAr: 'جودة البيانات والامتثال لمستويات الخدمة ومقاييس الالتزام بالعملية',
    metrics: [
      { name: 'SLA compliance rate', nameAr: 'معدل الامتثال لمستوى الخدمة', target: '>95%', owner: 'BDC Supervisor', ownerAr: 'مشرف BDC' },
      { name: 'Data completeness at capture', nameAr: 'اكتمال البيانات عند الالتقاط', target: '>80%', owner: 'CRM System', ownerAr: 'نظام CRM' },
      { name: 'Source attribution accuracy', nameAr: 'دقة إسناد المصدر', target: '100%', owner: 'Marketing Ops', ownerAr: 'عمليات التسويق' },
      { name: 'Duplicate detection rate', nameAr: 'معدل اكتشاف التكرار', target: '>95%', owner: 'CRM System', ownerAr: 'نظام CRM' },
      { name: 'Cadence completion rate', nameAr: 'معدل اكتمال التسلسل', target: '100%', owner: 'BDC Agent', ownerAr: 'وكيل BDC' },
    ],
  },
  {
    id: 'customer',
    label: 'Customer Experience',
    labelAr: 'تجربة العميل',
    icon: Star,
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    description: 'Customer satisfaction, NPS, and post-sale engagement',
    descriptionAr: 'رضا العملاء ومؤشر صافي المروجين والمشاركة بعد البيع',
    metrics: [
      { name: 'CSAT at delivery', nameAr: 'رضا العميل عند التسليم', target: '>4.5/5', owner: 'Delivery Coordinator', ownerAr: 'منسق التسليم' },
      { name: 'NPS at Day 30', nameAr: 'مؤشر المروجين - اليوم 30', target: '>50', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'Post-visit satisfaction (showroom)', nameAr: 'الرضا بعد زيارة المعرض', target: '>4.5/5', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'Referral generation rate', nameAr: 'معدل توليد الإحالات', target: '>15%', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'First service booking rate', nameAr: 'معدل حجز أول خدمة', target: '>80%', owner: 'Delivery Coordinator', ownerAr: 'منسق التسليم' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & Contracting',
    labelAr: 'التمويل والتعاقد',
    icon: Target,
    color: 'text-rose-700',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    description: 'Finance approval rates, deal cycle times, and contracting efficiency',
    descriptionAr: 'معدلات الموافقة على التمويل وأوقات دورة الصفقات وكفاءة التعاقد',
    metrics: [
      { name: 'Finance approval rate', nameAr: 'معدل الموافقة على التمويل', target: '>80%', owner: 'Finance Officer', ownerAr: 'مسؤول التمويل' },
      { name: 'Average deal cycle time', nameAr: 'متوسط وقت دورة الصفقة', target: '<5 days', owner: 'Sales Manager', ownerAr: 'مدير المبيعات' },
      { name: 'Quote turnaround time', nameAr: 'وقت تسليم العرض', target: '<2 hours', owner: 'Sales Advisor', ownerAr: 'مستشار المبيعات' },
      { name: 'Booking cancellation rate', nameAr: 'معدل إلغاء الحجز', target: '<5%', owner: 'Sales Manager', ownerAr: 'مدير المبيعات' },
      { name: 'On-time delivery rate', nameAr: 'معدل التسليم في الوقت المحدد', target: '>95%', owner: 'Delivery Coordinator', ownerAr: 'منسق التسليم' },
    ],
  },
];

// ── Stage funnel data ─────────────────────────────────────────────────────────

const funnelStages = [
  { label: 'Lead Captured', labelAr: 'تم التقاط العميل', pct: 100, color: 'bg-gray-400' },
  { label: 'First Contact Made', labelAr: 'تم الاتصال الأول', pct: 75, color: 'bg-blue-400' },
  { label: 'Qualified', labelAr: 'مؤهل', pct: 40, color: 'bg-cyan-500' },
  { label: 'Appointment Kept', labelAr: 'حضر الموعد', pct: 28, color: 'bg-teal-500' },
  { label: 'Quote Sent', labelAr: 'تم إرسال العرض', pct: 20, color: 'bg-emerald-500' },
  { label: 'Negotiation', labelAr: 'التفاوض', pct: 14, color: 'bg-lime-500' },
  { label: 'Booked / Won', labelAr: 'تم الحجز / الفوز', pct: 8, color: 'bg-green-600' },
];

// ── Component ──────────────────────────────────────────────────────────────────

const PerformanceOverview = () => {
  const ar = isAr();
  const crmStages = getCRMStages();
  const stageSLAs = getStageSLAs();

  const slaMap: Record<string, string> = {};
  stageSLAs.forEach(s => { slaMap[s.stageId] = s.targetDuration; });

  return (
    <div className="space-y-10 animate-fade-in">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {ar ? 'إدارة الأداء' : 'Performance Management'}
          </h1>
          <p className="mt-1 text-gray-500 text-sm">
            {ar
              ? 'إطار مؤشرات الأداء، بطاقات النتائج، وأهداف مستوى الخدمة عبر جميع مراحل مسار المبيعات'
              : 'KPI framework, scorecards, and SLA targets across the full lead pipeline'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/performance/kpis"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            {ar ? 'إطار KPI' : 'KPI Framework'}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/performance/scorecards"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Users className="h-4 w-4" />
            {ar ? 'بطاقات النتائج' : 'Scorecards'}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* KPI Category Cards */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary-600" />
          {ar ? 'فئات مؤشرات الأداء الرئيسية' : 'KPI Categories'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {kpiCategories.map(cat => (
            <Link
              key={cat.id}
              to="/performance/kpis"
              className={`rounded-2xl border ${cat.bgColor} ${cat.borderColor} p-5 hover:shadow-md transition-all group`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`rounded-xl p-2.5 bg-white shadow-sm`}>
                  <cat.icon className={`h-5 w-5 ${cat.color}`} />
                </div>
                <span className={`text-xs font-semibold ${cat.color} px-2 py-0.5 rounded-full bg-white`}>
                  {cat.metrics.length} {ar ? 'مؤشر' : 'KPIs'}
                </span>
              </div>
              <h3 className={`text-sm font-bold ${cat.color} mb-1`}>{ar ? cat.labelAr : cat.label}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{ar ? cat.descriptionAr : cat.description}</p>
              <div className="mt-3 pt-3 border-t border-white/60 flex items-center justify-between">
                <span className="text-xs text-gray-400">{ar ? 'أبرز الأهداف' : 'Top target'}: <span className={`font-semibold ${cat.color}`}>{cat.metrics[0].target}</span></span>
                <ChevronRight className={`h-4 w-4 ${cat.color} opacity-0 group-hover:opacity-100 transition-opacity`} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Two-column: Funnel + Stage SLA Targets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Sales Funnel (indicative) */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary-600" />
            {ar ? 'قمع المبيعات (معايير الصناعة)' : 'Sales Funnel (Industry Benchmarks)'}
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
            {funnelStages.map((stage, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{ar ? stage.labelAr : stage.label}</span>
                  <span className="text-sm font-bold text-gray-900">{stage.pct}%</span>
                </div>
                <div className="h-6 bg-gray-100 rounded-lg overflow-hidden">
                  <div
                    className={`h-full ${stage.color} rounded-lg transition-all duration-500 flex items-center justify-end pr-2`}
                    style={{ width: `${stage.pct}%` }}
                  >
                    {stage.pct > 15 && (
                      <span className="text-white text-xs font-bold">{stage.pct}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
              {ar
                ? '* معدلات التحويل المشار إليها هي معايير الصناعة للسيارات السعودية. استخدم بياناتك الفعلية لتحديث هذه الأرقام.'
                : '* Conversion rates shown are Saudi automotive industry benchmarks. Replace with your actual CRM data.'}
            </p>
          </div>
        </section>

        {/* Stage SLA Targets */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary-600" />
            {ar ? 'أهداف مستوى الخدمة لكل مرحلة' : 'Stage SLA Targets'}
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {crmStages.map((stage, idx) => {
              const sla = stageSLAs.find(s => s.stageId === stage.meta.id);
              return (
                <Link
                  key={stage.meta.id}
                  to={`/sla-catalogue/stage/stage_sla_${stage.meta.id}`}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${idx < crmStages.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <span
                    className="w-6 h-6 rounded-md text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: stage.meta.color }}
                  >
                    {stage.meta.order}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {ar ? stage.meta.titleAr : stage.meta.title}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {sla ? (
                      <>
                        <p className="text-sm font-bold text-amber-700">{sla.targetDuration}</p>
                        <p className="text-xs text-gray-400">max {sla.maxDuration}</p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400">{ar ? 'يُرجى المراجعة' : 'See SLA catalogue'}</p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      {/* Key Metrics Summary Table */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary-600" />
          {ar ? 'ملخص المؤشرات الرئيسية' : 'Key Performance Targets at a Glance'}
        </h2>
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left rtl:text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {ar ? 'المؤشر' : 'Metric'}
                  </th>
                  <th className="text-left rtl:text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {ar ? 'الهدف' : 'Target'}
                  </th>
                  <th className="text-left rtl:text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {ar ? 'المسؤول' : 'Owner'}
                  </th>
                  <th className="text-left rtl:text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {ar ? 'الفئة' : 'Category'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {kpiCategories.flatMap(cat =>
                  cat.metrics.map((m, mIdx) => (
                    <tr
                      key={`${cat.id}-${mIdx}`}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-5 py-3 text-sm text-gray-800 font-medium">
                        {ar ? m.nameAr : m.name}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${cat.bgColor} ${cat.color} border ${cat.borderColor}`}>
                          {m.target}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{ar ? m.ownerAr : m.owner}</td>
                      <td className="px-5 py-3 text-xs text-gray-500">{ar ? cat.labelAr : cat.label}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Navigation Footer */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <Link
          to="/performance/kpis"
          className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-2xl px-5 py-4 hover:bg-primary-100 transition-colors group"
        >
          <div>
            <p className="text-sm font-bold text-primary-800">{ar ? 'إطار مؤشرات الأداء الكاملة' : 'Full KPI Framework'}</p>
            <p className="text-xs text-primary-600 mt-0.5">{ar ? 'جميع المؤشرات مصنفة حسب المرحلة' : 'All KPIs organised by pipeline stage'}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-primary-600 group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/performance/scorecards"
          className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-2xl px-5 py-4 hover:bg-violet-100 transition-colors group"
        >
          <div>
            <p className="text-sm font-bold text-violet-800">{ar ? 'بطاقات نتائج الفريق' : 'Team Scorecards'}</p>
            <p className="text-xs text-violet-600 mt-0.5">{ar ? 'الأداء حسب الدور وبطاقات التقييم' : 'Role-by-role performance targets & cards'}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-violet-600 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

    </div>
  );
};

export default PerformanceOverview;
